import { QueryClient } from '@tanstack/react-query'

/**
 * One instance for the whole app, exported so the session stores can clear it on logout — not
 * just so `App.tsx` can hand it to the provider.
 *
 * Without that, a cached answer from one session survives in memory into the next: a cashier
 * closes a shift, logs out, logs back in (or a different cashier does, on the same device) and
 * the checkout screen would show whatever `['shift', registerId]` last resolved to, served
 * straight from cache rather than fetched fresh, for as long as `staleTime` allows. A hard
 * refresh wipes this cache incidentally, which is why the symptom always seems to disappear
 * after one — the actual bug is that nothing before it forces a real refetch.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A POS runs on a shop's connection; a failed read should retry once, not hammer it.
      retry: 1,

      // Nothing here is ever treated as still-good without asking. A till is one of several
      // devices working the same books — another cashier's sale, a manager's pay-out, the
      // owner moving cash to the safe all happen elsewhere and this device is never told. A
      // staleTime meant "for the next thirty seconds, show what we last saw", which is how
      // opening the Cash tab could report a drawer figure that had already moved and only a
      // manual refresh would correct.
      staleTime: 0,

      // Cached data still renders immediately, so switching tabs stays instant — this refetches
      // behind it and replaces the figure as soon as the answer lands, rather than leaving the
      // old one there because the query was mounted recently.
      refetchOnMount: 'always',

      // Coming back to the till after serving a customer is exactly when the screen is most
      // likely to be out of date.
      refetchOnWindowFocus: true,
    },
  },
})
